import { brand, routes } from '../site';

/**
 * Yasal metinler (/yasal/*). Kaynak: "sözleşmeler" klasöründeki sekiz Word
 * belgesi (3 Ekim 2026 sürümü; Mesafeli Satış, Ön Bilgilendirme ve İptal & İade
 * 9 Ekim 2026 sürümü); metinler birebir aktarıldı.
 *
 * Satır içi işaretler: `**kalın**` ve `[etiket](adres)`. Sayfa bunları
 * biçimlendirir. Başka bir yasal metne yapılan atıflar `doc()` ile bağlantıya
 * çevrilir; sitede henüz sayfası olmayan metinler (Alt İşleyici Listesi,
 * Yapay Zekâ Özellikleri Açık Rıza Metni, Veri Saklama ve İmha Politikası)
 * yalnızca kalın yazılır.
 */
export type LegalBlock =
  | { type: 'p'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'table'; head: string[]; rows: string[][] };

export interface LegalSection {
  title: string;
  blocks: LegalBlock[];
}

export type LegalKey =
  | 'kvkk'
  | 'gizlilik'
  | 'cerez'
  | 'kullanim'
  | 'mesafeli'
  | 'onBilgilendirme'
  | 'iptalIade'
  | 'guvenlik';

export interface LegalDoc {
  key: LegalKey;
  href: string;
  title: string;
  /** Yasal merkez kartındaki kısa açıklama. */
  summary: string;
  updated: string;
  /** Başlık altındaki giriş paragrafları; boş olabilir. */
  intro: string[];
  sections: LegalSection[];
}

const p = (text: string): LegalBlock => ({ type: 'p', text });
const list = (...items: string[]): LegalBlock => ({ type: 'list', items });

const UPDATED = '3 Ekim 2026';
/** 9 Ekim 2026 revizyonu: aylık abonelik kaldırıldı, yalnızca yıllık (ya da teklifle belirlenen süreli) lisans. */
const UPDATED_LICENSE = '9 Ekim 2026';

const titles: Record<LegalKey, string> = {
  kvkk: 'KVKK Aydınlatma Metni',
  gizlilik: 'Gizlilik Politikası',
  cerez: 'Çerez Politikası',
  kullanim: 'Kullanım Şartları',
  mesafeli: 'Mesafeli Satış ve Abonelik Sözleşmesi',
  onBilgilendirme: 'Ön Bilgilendirme Formu',
  iptalIade: 'İptal & İade Koşulları',
  guvenlik: 'Veri Güvenliği Politikası',
};

/** Başka bir yasal metne bağlantı; etiket verilmezse metnin başlığı kullanılır. */
const doc = (key: LegalKey, label = titles[key]) => `[${label}](${routes.yasal[key]})`;

const mail = `[${brand.email}](mailto:${brand.email})`;

const company = {
  name: 'Z Hesap Yazılım Anonim Şirketi',
  address: 'Kirazlıdere Mah. Eski Ankara Cad. A-1 Blok No:4A İç Kapı No:3, Çekmeköy / İstanbul',
  taxOffice: 'Sarıgazi V.D.',
  taxNo: '9961481853',
  mersis: '0996148185300001',
  phone: '+90 553 481 58 42',
  web: 'www.zhesap.com',
} as const;

/** Satıcı / veri sorumlusu künyesi; üç belgede aynı liste geçiyor. */
const companyList = (opts: { web?: boolean; phoneFirst?: boolean } = {}): LegalBlock => {
  const base = [
    `**Unvan:** ${company.name}`,
    `**Adres:** ${company.address}`,
    `**Vergi Dairesi / No:** ${company.taxOffice} / ${company.taxNo}`,
    `**MERSİS No:** ${company.mersis}`,
  ];
  const contact = opts.phoneFirst
    ? [`**Telefon:** ${company.phone}`, `**E-posta:** ${mail}`]
    : [`**E-posta:** ${mail}`, `**Telefon:** ${company.phone}`];
  const web = opts.web ? [`**Web:** ${company.web}`] : [];
  return list(...base, ...contact, ...web);
};

export const legalHub = {
  href: routes.yasal.root,
  crumb: 'Yasal Merkez',
  title: 'Yasal',
  titleAccent: 'Merkez',
  lead: 'Sözleşmelerimiz, politikalarımız ve kişisel verilerin korunmasına ilişkin metinlerimiz. Metinler birbirini tamamlar ve birlikte okunur.',
  updatedLabel: 'Son güncelleme',
  updated: UPDATED_LICENSE,
  groups: [
    { title: 'Kişisel Veriler ve Gizlilik', keys: ['kvkk', 'gizlilik', 'cerez', 'guvenlik'] as LegalKey[] },
    { title: 'Hizmet ve Abonelik', keys: ['kullanim', 'mesafeli', 'onBilgilendirme', 'iptalIade'] as LegalKey[] },
  ],
  readLabel: 'Metni oku',
  // Künye satırları site ayarlarından gelir (hooks/useCms → useSiteInfo().legal).
  company: { title: 'Veri sorumlusu ve satıcı' },
  contact: {
    title: 'Sorunuz mu var?',
    desc: 'Kişisel verileriniz, aboneliğiniz ya da sözleşmelerle ilgili her soru için bize yazabilirsiniz.',
    cta: { label: 'Bize Yaz', href: routes.iletisim },
  },
} as const;

/** Belge sayfasındaki ortak etiketler. */
export const legalPageCopy = {
  crumb: 'Yasal Merkez',
  updatedLabel: 'Son güncelleme',
  navTitle: 'Yasal metinler',
  tocTitle: 'Bu sayfada',
  backLabel: 'Tüm yasal metinler',
  contactTitle: 'Sorunuz mu var?',
  contactDesc: 'Bu metinle ilgili sorularınız için bize yazabilirsiniz.',
} as const;

/**
 * Form onaylarından açılan yasal metin penceresi: kullanıcı formdan ayrılmadan
 * metni okur; isterse tam sayfaya geçer.
 */
export const legalModalCopy = {
  crumb: 'Yasal Merkez',
  updatedLabel: 'Son güncelleme',
  openPage: 'Tam sayfada aç',
  close: 'Kapat',
  closeLabel: 'Pencereyi kapat',
} as const;

export const legalDocs: LegalDoc[] = [
  {
    key: 'kvkk',
    href: routes.yasal.kvkk,
    title: titles.kvkk,
    summary: 'Hangi kişisel verileri hangi amaçla işlediğimiz, kimlerle paylaştığımız ve KVKK kapsamındaki haklarınız.',
    updated: UPDATED,
    intro: [
      'Bu Aydınlatma Metni, 6698 sayılı Kişisel Verilerin Korunması Kanunu (“KVKK”) uyarınca, Z Hesap Yazılım Anonim Şirketi (“Z Hesap” veya “Şirket”) tarafından veri sorumlusu sıfatıyla; web sitesi ziyaretçileri, üyeler, müşteriler, müşteri çalışanları ve iş ortaklarının kişisel verilerinin işlenmesine ilişkin bilgilendirme amacıyla hazırlanmıştır.',
    ],
    sections: [
      { title: '1. Veri Sorumlusu', blocks: [companyList({ web: true })] },
      {
        title: '2. İşlenen Kişisel Veriler',
        blocks: [
          list(
            '**Kimlik:** ad, soyad; fatura düzenlenmesi için gerekmesi hâlinde T.C. kimlik numarası.',
            '**İletişim:** telefon, e-posta, adres.',
            '**Müşteri işlem:** abonelik ve paket bilgileri, talep ve destek kayıtları.',
            '**Finans:** fatura ve ödeme işlem bilgileri. Kart bilgileri Z Hesap tarafından saklanmaz; ödeme kuruluşu nezdinde işlenir.',
            '**İşlem güvenliği:** IP adresi, oturum ve log kayıtları.',
            '**Kullanım:** site, uygulama ve tarayıcı eklentisi kullanım verileri, çerez verileri.',
            '**Kurumsal kişi bilgileri:** firma unvanı, vergi numarası, yetkili kişi bilgileri.'
          ),
        ],
      },
      {
        title: '3. İşleme Amaçları',
        blocks: [
          list(
            'Z Hesap hizmetlerinin (web uygulaması ve tarayıcı eklentisi) sunulması, çalıştırılması ve iyileştirilmesi,',
            'Üyelik, kimlik doğrulama ve hesap yönetimi,',
            'Abonelik, faturalandırma ve tahsilat süreçlerinin yürütülmesi,',
            'Talep, öneri ve şikâyetlerin yönetimi, destek ve iletişim faaliyetleri,',
            'Yazılım hatalarının tespiti ve giderilmesi, istatistiksel değerlendirmeler,',
            'Bilgi güvenliğinin sağlanması ve kötüye kullanımın önlenmesi,',
            'Hukuki yükümlülüklerin yerine getirilmesi ve yetkili mercilere bilgi verilmesi,',
            'Açık rıza verilmesi hâlinde tanıtım ve pazarlama faaliyetleri ile analitik ve pazarlama çerezlerinin kullanılması.'
          ),
        ],
      },
      {
        title: '4. Hukuki Sebepler',
        blocks: [
          p(
            'Kişisel verileriniz KVKK m.5 ve m.6 kapsamında; sözleşmenin kurulması ve ifası (m.5/2-c), hukuki yükümlülüğün yerine getirilmesi (m.5/2-ç), bir hakkın tesisi, kullanılması veya korunması (m.5/2-e), meşru menfaat (m.5/2-f) ve gerekli hâllerde açık rıza (m.5/1) hukuki sebeplerine dayanılarak işlenir.'
          ),
        ],
      },
      {
        title: '5. Toplama Yöntemleri',
        blocks: [
          p(
            'Kişisel verileriniz; web sitesi, web uygulaması, tarayıcı eklentisi, çerezler, elektronik formlar, e-posta ve destek kanalları ile sözleşme ve ticari belgeler aracılığıyla, kısmen veya tamamen otomatik yollarla toplanır.'
          ),
        ],
      },
      {
        title: '6. Aktarım',
        blocks: [
          p(
            'Kişisel verileriniz; barındırma ve altyapı sağlayıcıları, ödeme kuruluşları, e-posta ve bildirim hizmeti sağlayıcıları, analitik hizmet sağlayıcıları, hukuki ve mali danışmanlar ile yetkili kamu kurum ve kuruluşlarına, KVKK m.8 ve m.9 çerçevesinde ve yalnızca ilgili amaçla sınırlı olarak aktarılabilir. Kullanılan hizmet sağlayıcı kategorileri **Alt İşleyici Listesi**’nde yayımlanır. Verileriniz pazarlama amacıyla üçüncü kişilere satılmaz veya kiralanmaz.'
          ),
        ],
      },
      {
        title: '7. Yurt Dışına Aktarım',
        blocks: [
          p(
            'Barındırma, bulut, analitik ve yapay zekâ teknolojisi sağlayıcılarımızın sunucuları yurt dışında bulunabilir. Yurt dışına aktarım KVKK m.9 uyarınca; yeterlilik kararı bulunması hâlinde bu karara, bulunmaması hâlinde standart sözleşme gibi uygun güvencelere, bunların da bulunmadığı hâllerde yalnızca KVKK m.9/6’da sayılan arızi hâllere dayanılarak gerçekleştirilir.'
          ),
          p(
            'Yapay zekâ destekli özelliklerde kişisel veri paylaşılmaz; kişiyi tanımlayabilecek alanlar işleme öncesinde anonimleştirilir veya maskelenir. Ayrıntılar **Yapay Zekâ Özellikleri Açık Rıza Metni**’nde yer alır.'
          ),
        ],
      },
      {
        title: '8. Veri Güvenliği, Saklama ve İmha',
        blocks: [
          p(
            `Kişisel verilerinizin güvenliği için KVKK m.12 uyarınca gerekli teknik ve idari tedbirler alınır (${doc('guvenlik')}). Verileriniz işleme amacının gerektirdiği ve mevzuatta öngörülen süreler boyunca saklanır; süre sonunda silinir, yok edilir veya anonim hâle getirilir (**Veri Saklama ve İmha Politikası**).`
          ),
        ],
      },
      {
        title: '9. Haklarınız (KVKK m.11)',
        blocks: [
          p(
            'Kişisel verilerinizin işlenip işlenmediğini öğrenme, işlenmişse bilgi talep etme, işleme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme, yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme, eksik veya yanlış işlenmişse düzeltilmesini isteme, KVKK m.7 çerçevesinde silinmesini veya yok edilmesini isteme, bu işlemlerin aktarıldığı üçüncü kişilere bildirilmesini isteme, münhasıran otomatik sistemlerle analiz edilmesi sonucu aleyhinize bir sonuç çıkmasına itiraz etme ve kanuna aykırı işleme nedeniyle zarara uğramanız hâlinde zararın giderilmesini talep etme haklarına sahipsiniz.'
          ),
        ],
      },
      {
        title: '10. Başvuru',
        blocks: [
          p(
            `Haklarınızı kullanmak için talebinizi; yukarıdaki adrese yazılı olarak, güvenli elektronik imza veya mobil imza ile ya da sistemimizde kayıtlı e-posta adresinizden **${mail}** adresine iletebilirsiniz. Başvurunuz KVKK m.13 uyarınca en geç 30 gün içinde ücretsiz olarak sonuçlandırılır; işlemin ayrıca bir maliyet gerektirmesi hâlinde Kurul tarafından belirlenen tarifedeki ücret alınabilir. Başvurunuzun reddedilmesi, verilen cevabın yetersiz bulunması veya süresinde cevap verilmemesi hâlinde, cevabı öğrendiğiniz tarihten itibaren 30 gün ve her hâlde başvuru tarihinden itibaren 60 gün içinde Kişisel Verileri Koruma Kurulu’na şikâyette bulunabilirsiniz.`
          ),
        ],
      },
      {
        title: '11. Güncelleme',
        blocks: [p('Bu metin gerektiğinde güncellenebilir. Güncel sürüm her zaman www.zhesap.com üzerinde yayımlanır.')],
      },
    ],
  },
  {
    key: 'gizlilik',
    href: routes.yasal.gizlilik,
    title: titles.gizlilik,
    summary: 'Web sitesi, uygulama ve tarayıcı eklentisinde verilerin nasıl toplandığı, kullanıldığı ve korunduğu.',
    updated: UPDATED,
    intro: [
      'Bu Gizlilik Politikası, Z Hesap Yazılım Anonim Şirketi (“Z Hesap” veya “Şirket”) tarafından işletilen www.zhesap.com web sitesi, Z Hesap web uygulaması ve Z Hesap tarayıcı eklentisi (birlikte “Hizmetler”) aracılığıyla işlenen verilerin nasıl toplandığını, kullanıldığını ve korunduğunu açıklar.',
    ],
    sections: [
      {
        title: '1. Veri Sorumlusu',
        blocks: [
          p(
            `${company.name} — ${company.address} — ${company.taxOffice} ${company.taxNo} — MERSİS ${company.mersis} — ${mail}`
          ),
        ],
      },
      {
        title: '2. İşlediğimiz Veriler',
        blocks: [
          list(
            '**Kimlik ve iletişim:** ad, soyad, e-posta, telefon, unvan ve şirket bilgisi.',
            '**Hesap ve üyelik:** kullanıcı adı, özetlenmiş (hash) parola, rol ve yetkiler, abonelik ve paket bilgileri.',
            '**Fatura ve ödeme:** fatura bilgileri, vergi numarası, ödeme işlem kayıtları. Kart bilgileri Z Hesap tarafından saklanmaz.',
            '**E-ticaret entegrasyon verileri:** bağladığınız pazaryeri, e-ticaret sitesi, ERP ve kargo hesaplarına ait sipariş, ürün, fiyat, maliyet, stok, kampanya, iade, hakediş, kargo ve mağaza performans verileri. Bu veriler ağırlıklı olarak işletmenize ait ticari verilerdir.',
            '**Alıcı verileri:** sipariş ve müşteri sorusu verileri içinde yer alabilen, sizin müşterilerinize ait sınırlı bilgiler. Bu veriler bakımından veri sorumlusu sizsiniz; Z Hesap yalnızca sizin adınıza ve talimatınız doğrultusunda **veri işleyen** olarak hareket eder.',
            '**Kullanım ve işlem:** platform içi hareketler, ayarlar, destek yazışmaları.',
            '**Teknik:** IP adresi, cihaz ve tarayıcı bilgisi, log kayıtları, çerez verileri.'
          ),
        ],
      },
      {
        title: '3. Kullanım Amaçları',
        blocks: [
          p(
            'Hesabınızın oluşturulması ve yönetimi; Hizmetlerin sunulması ve iyileştirilmesi; e-ticaret verilerinizin analizi, raporlanması ve karar destek içgörülerinin üretilmesi; abonelik ve faturalandırma; destek ve iletişim; bilgi güvenliği ve kötüye kullanımın önlenmesi; hukuki yükümlülüklerin yerine getirilmesi; açık rızanız hâlinde pazarlama faaliyetleri.'
          ),
        ],
      },
      {
        title: '4. Hukuki Sebepler',
        blocks: [
          p(
            'Sözleşmenin kurulması ve ifası, hukuki yükümlülük, meşru menfaat ve gerekli hâllerde açık rıza (KVKK m.5 ve m.6). Avrupa Birliği’nde bulunan kullanıcılar bakımından, uygulanabildiği ölçüde Genel Veri Koruma Tüzüğü (“GDPR”) m.6.'
          ),
        ],
      },
      {
        title: '5. Paylaşım',
        blocks: [
          p(
            'Verileriniz yalnızca hizmetin gerektirdiği ölçüde ve gizlilik yükümlülüğü altında; barındırma ve altyapı sağlayıcıları, ödeme kuruluşları, bağladığınız e-ticaret platformları ve pazaryerleri, e-posta ve bildirim sağlayıcıları, analitik sağlayıcılar, bulut yapay zekâ teknolojisi sağlayıcıları ve yetkili kamu kurumlarıyla paylaşılabilir. Verileriniz pazarlama amacıyla üçüncü kişilere satılmaz veya kiralanmaz. Sağlayıcı kategorileri **Alt İşleyici Listesi**’nde yayımlanır.'
          ),
        ],
      },
      {
        title: '6. Yapay Zekâ Destekli Özellikler',
        blocks: [
          p(
            'Z Hesap; **Z’eki Şirket Asistanı** (soru-cevap, içgörü üretimi ve rapor yorumlama), **AI Müşteri İlişkileri** (pazaryeri müşteri sorularına yapay zekâ destekli yanıt) ve ürün yorumu analizi gibi yapay zekâ destekli özellikler sunar.'
          ),
          list(
            '**Kişisel veri paylaşılmaz:** Ad, soyad, iletişim bilgisi, adres, sipariş ve hesap kimliği gibi kişiyi tanımlayabilecek alanlar, yapay zekâ teknolojilerine gönderilmeden önce anonimleştirilir veya maskelenir. Müşteri sorusu ve yorum gibi serbest metinler de işleme öncesinde temizlenir.',
            '**Ticari verileriniz korunur:** Ürün, fiyat, stok, kampanya ve performans gibi ticari veriler yalnızca ilgili özelliğin çalışması için gereken ölçüde işlenir. Sağlayıcılarla Veri İşleme Sözleşmesi (DPA), gerektiğinde Standart Sözleşme Hükümleri ve “verilerin model eğitiminde kullanılmaması” taahhüdü bulunur.',
            '**İsteğe bağlıdır:** Yapay zekâ özellikleri yalnızca açık rızanızla etkinleşir ve rızanızı uygulama içinden dilediğiniz an geri alabilirsiniz. Ayrıntılar: **Yapay Zekâ Özellikleri Açık Rıza Metni**.'
          ),
        ],
      },
      {
        title: '7. Yurt Dışına Aktarım',
        blocks: [
          p(
            'Bazı sağlayıcılarımızın sunucuları yurt dışında bulunabilir. Aktarımlar KVKK m.9 ve uygulanabildiği ölçüde GDPR uyarınca; standart sözleşme gibi uygun güvenceler, şifreleme, anonimleştirme ve erişim kontrolü gibi tedbirlerle ve gerekli hâllerde açık rızanıza dayanılarak gerçekleştirilir.'
          ),
        ],
      },
      {
        title: '8. Saklama',
        blocks: [
          p(
            'Verileriniz işleme amacının gerektirdiği ve mevzuatta öngörülen süreler boyunca saklanır. Ayrıntılar: **Veri Saklama ve İmha Politikası**.'
          ),
        ],
      },
      {
        title: '9. Güvenlik',
        blocks: [
          p(
            `Şifreleme, erişim kontrolü, ağ güvenliği, log yönetimi ve düzenli yedekleme gibi teknik ve idari tedbirler uygulanır. Ayrıntılar: ${doc('guvenlik')}.`
          ),
        ],
      },
      {
        title: '10. Haklarınız',
        blocks: [
          p(
            `KVKK m.11 ve uygulanabildiği ölçüde GDPR kapsamındaki haklarınızı (bilgi talep etme, düzeltme, silme, itiraz, veri taşınabilirliği vb.) **${mail}** adresine başvurarak kullanabilirsiniz. Hesap kapatma ve veri silme süreçlerini uygulama içinden de başlatabilirsiniz. Başvurular en geç 30 gün içinde sonuçlandırılır.`
          ),
        ],
      },
      {
        title: '11. Çerezler',
        blocks: [
          p(
            `Web sitemiz zorunlu çerezlerin yanı sıra, yalnızca onay vermeniz hâlinde analitik ve pazarlama çerezleri kullanır. Ayrıntılar: ${doc('cerez')}.`
          ),
        ],
      },
      {
        title: '12. Değişiklikler',
        blocks: [
          p(
            'Bu Politika güncellenebilir; güncel sürüm bu sayfada “Son güncelleme” tarihiyle yayımlanır. Önemli değişikliklerde kayıtlı kullanıcılar e-posta veya uygulama içi bildirimle bilgilendirilir.'
          ),
        ],
      },
    ],
  },
  {
    key: 'cerez',
    href: routes.yasal.cerez,
    title: titles.cerez,
    summary: 'Hangi çerezleri neden kullandığımız, üçüncü taraf çerezler, saklama süreleri ve tercihlerinizi yönetme.',
    updated: UPDATED,
    intro: [
      `Z Hesap Yazılım Anonim Şirketi (“Z Hesap” veya “Şirket”) olarak www.zhesap.com web sitesi ve Z Hesap web uygulamasında çerezler ve benzeri teknolojiler kullanmaktayız. Bu Politika, hangi çerezleri neden kullandığımızı ve tercihlerinizi nasıl yönetebileceğinizi açıklar. Çerezler aracılığıyla işlenen kişisel veriler, 6698 sayılı KVKK ve ${doc('gizlilik')} ile uyumlu olarak işlenir.`,
    ],
    sections: [
      {
        title: '1. Çerez Nedir?',
        blocks: [
          p(
            'Çerezler, bir web sitesini ziyaret ettiğinizde tarayıcınız aracılığıyla cihazınıza kaydedilen küçük metin dosyalarıdır. Oturumunuzun sürdürülmesi, tercihlerinizin hatırlanması ve sitenin nasıl kullanıldığının anlaşılması gibi amaçlarla kullanılır.'
          ),
        ],
      },
      {
        title: '2. Kullandığımız Çerez Kategorileri',
        blocks: [
          {
            type: 'table',
            head: ['Kategori', 'Amaç', 'Onay'],
            rows: [
              [
                'Zorunlu çerezler',
                'Oturum yönetimi, güvenlik, çerez tercihlerinizin saklanması, sitenin temel işlevleri',
                'Gerekmez, kapatılamaz',
              ],
              ['İşlevsel çerezler', 'Dil ve görünüm gibi tercihlerin hatırlanması', 'Onayınıza bağlıdır'],
              [
                'Analitik çerezler',
                'Ziyaret istatistikleri ve sitenin nasıl kullanıldığının ölçülmesi (Google Analytics)',
                'Onayınıza bağlıdır',
              ],
              [
                'Pazarlama çerezleri',
                'Reklam ve kampanyaların ölçülmesi ve ilgi alanlarınıza göre kişiselleştirilmesi (Google Ads, Meta Pixel)',
                'Onayınıza bağlıdır',
              ],
            ],
          },
          p('Zorunlu çerezler dışındaki çerezler, siz çerez bandı üzerinden onay vermedikçe çalıştırılmaz.'),
        ],
      },
      {
        title: '3. Üçüncü Taraf Çerezler',
        blocks: [
          list(
            '**Google Analytics (Google LLC / Google Ireland Ltd.):** Sitenin kullanım istatistiklerini ölçmek için, yalnızca analitik çerezlere onay vermeniz hâlinde kullanılır. Google’ın sunucuları yurt dışında bulunduğundan bu veriler yurt dışına aktarılır. Google Analytics’i devre dışı bırakmak için Google’ın tarayıcı eklentisini kullanabilirsiniz: [tools.google.com/dlpage/gaoptout](https://tools.google.com/dlpage/gaoptout)',
            '**Google Ads (Google LLC / Google Ireland Ltd.):** Google reklamlarımızın etkinliğini ölçmek (örneğin bir reklamı tıklayan ziyaretçinin sitemizde kayıt olup olmadığını görmek) ve sitemizi daha önce ziyaret etmiş kişilere Google ağında reklam göstermek (yeniden pazarlama) amacıyla, yalnızca pazarlama çerezlerine onay vermeniz hâlinde kullanılır. Bu kapsamda Google etiketi aracılığıyla “_gcl_” ve “_gac_” gibi çerezler ile Google alan adlarında “IDE” gibi reklam çerezleri kullanılabilir; bu çerezler reklam tıklama kimliği, ziyaret edilen sayfalar, gerçekleştirilen işlemler ile cihaz ve tarayıcı bilgileri gibi verileri işler. Google’ın sunucuları yurt dışında bulunduğundan bu veriler yurt dışına aktarılır. Google’ın reklam amacıyla veri işlemesine ilişkin bilgi için [policies.google.com/technologies/ads](https://policies.google.com/technologies/ads) adresini inceleyebilir, kişiselleştirilmiş reklam tercihlerinizi [myadcenter.google.com](https://myadcenter.google.com) üzerinden yönetebilirsiniz.',
            '**Meta Pixel (Meta Platforms Ireland Ltd. / Meta Platforms, Inc.):** Facebook ve Instagram reklamlarımızın etkinliğini ölçmek, sitemizi daha önce ziyaret etmiş kişilere bu platformlarda reklam göstermek ve reklam kitleleri oluşturmak amacıyla, yalnızca pazarlama çerezlerine onay vermeniz hâlinde kullanılır. Bu kapsamda “_fbp” ve “_fbc” çerezleri ile Meta alan adlarında “fr” çerezi kullanılabilir; bu çerezler ziyaret edilen sayfalar, gerçekleştirilen işlemler (örneğin kayıt olma veya satın alma), reklam tıklama kimliği ile cihaz ve tarayıcı bilgileri gibi verileri işler. Meta’nın sunucuları yurt dışında bulunduğundan bu veriler yurt dışına aktarılır. Meta’nın veri işleme faaliyetleri hakkında bilgi için [facebook.com/privacy/policy](https://www.facebook.com/privacy/policy) adresini inceleyebilir, reklam tercihlerinizi Facebook veya Instagram hesabınızdaki “Reklam Tercihleri” bölümünden yönetebilirsiniz.'
          ),
        ],
      },
      {
        title: '4. Saklama Süreleri',
        blocks: [
          list(
            '**Oturum çerezleri:** tarayıcı kapatıldığında silinir.',
            '**Kalıcı çerezler:** amacına göre belirlenen süre boyunca, en fazla 2 yıl saklanır. Google Analytics çerezlerinin süresi en fazla 2 yıldır. Google Ads dönüşüm çerezleri (“_gcl_”, “_gac_”) 1 yıl, Google reklam çerezleri (“IDE”) en fazla 24 ay, Meta Pixel çerezleri (“_fbp”, “_fbc”, “fr”) ise en fazla 1 yıl süreyle saklanır.'
          ),
        ],
      },
      {
        title: '5. Tercihlerinizi Yönetme',
        blocks: [
          p(
            'Çerez tercihlerinizi sitemizin altındaki **“Çerez Tercihleri”** bağlantısından dilediğiniz zaman değiştirebilir, verdiğiniz onayı geri alabilirsiniz. Ayrıca tarayıcı ayarlarınızdan çerezleri silebilir veya engelleyebilirsiniz. Zorunlu çerezlerin engellenmesi hâlinde sitenin bazı bölümleri çalışmayabilir.'
          ),
        ],
      },
      {
        title: '6. Tarayıcı Eklentisi',
        blocks: [
          p(
            'Z Hesap tarayıcı eklentisi çerez kullanmaz. Yalnızca tarayıcının eklentiye özel yerel depolama alanını kullanır ve burada oturum bilgisi, eklentinin açık/kapalı tercihi ve isteğe bağlı olarak hatırlanan e-posta adresini saklar. Eklenti yalnızca “storage” ve “activeTab” izinlerini kullanır; gezinme geçmişinizi izlemez. Bu veriler oturumu kapattığınızda veya eklentiyi kaldırdığınızda silinir.'
          ),
        ],
      },
      {
        title: '7. Hukuki Sebep',
        blocks: [
          p(
            'Zorunlu çerezler, sözleşmenin kurulması ve ifası ile meşru menfaatimize (KVKK m.5/2-c ve f) dayanır. Zorunlu olmayan çerezler (işlevsel, analitik ve pazarlama) yalnızca açık rızanıza (KVKK m.5/1) dayanılarak kullanılır. Yurt dışına aktarım KVKK m.9 uyarınca gerçekleştirilir.'
          ),
        ],
      },
      {
        title: '8. Haklarınız ve İletişim',
        blocks: [
          p(
            `KVKK m.11 kapsamındaki haklarınız ve başvuru yöntemi için ${doc('kvkk')}’ni inceleyebilir, **${mail}** adresinden bize ulaşabilirsiniz.`
          ),
        ],
      },
      { title: '9. Değişiklikler', blocks: [p('Bu Politika güncellenebilir; güncel sürüm bu sayfada yayımlanır.')] },
    ],
  },
  {
    key: 'kullanim',
    href: routes.yasal.kullanim,
    title: titles.kullanim,
    summary: 'Web sitesi, uygulama ve tarayıcı eklentisini kullanmanın koşulları; hesap güvenliği, kullanım kuralları ve sorumluluk.',
    updated: UPDATED,
    intro: [],
    sections: [
      {
        title: '1. Taraflar ve Kapsam',
        blocks: [
          p(
            `Bu Kullanım Şartları, Z Hesap Yazılım Anonim Şirketi (“Z Hesap”) tarafından işletilen www.zhesap.com web sitesi, Z Hesap web uygulaması ve tarayıcı eklentisinin (“Hizmetler”) kullanımına ilişkin koşulları düzenler. Hizmetlere erişen veya Hizmetleri kullanan herkes (“Kullanıcı”) bu şartları kabul etmiş sayılır. Ücretli abonelikler ayrıca ${doc('mesafeli')} ve ${doc('iptalIade')}’na tabidir.`
          ),
        ],
      },
      {
        title: '2. Hizmetin Niteliği',
        blocks: [
          p(
            'Z Hesap; pazaryerlerinde, kendi e-ticaret sitesinde veya birden fazla kanalda satış yapan işletmeler için kârlılık analizi, finans ve mutabakat kontrolü, kampanya kârlılığı, stok ve rekabet analizi ile yapay zekâ destekli karar destek hizmetleri sunan bulut tabanlı (SaaS) bir yazılımdır. Hizmetler **yalnızca ticari veya mesleki amaçla** kullanılmak üzere sunulur.'
          ),
        ],
      },
      {
        title: '3. Hesap ve Güvenlik',
        blocks: [
          list(
            'Kullanıcı, kayıt sırasında verdiği bilgilerin doğru ve güncel olduğunu kabul eder.',
            'Her kullanıcı hesabı tek bir kişiye aittir ve aynı anda yalnızca tek oturumla kullanılabilir. Hesap bilgilerinin paylaşılması yasaktır.',
            `Hesap şifresinin, kullanıcı yetkilerinin, API anahtarlarının ve entegrasyon bilgilerinin güvenliğinden Kullanıcı sorumludur. Yetkisiz kullanımı fark eden Kullanıcı, durumu derhâl ${mail} adresine bildirir.`
          ),
        ],
      },
      {
        title: '4. Kullanım Kuralları',
        blocks: [
          p(
            'Kullanıcı; Hizmetleri hukuka aykırı amaçlarla kullanamaz, tersine mühendislik yapamaz, yazılımı kopyalayamaz, kiralayamaz veya üçüncü kişilere alt lisanslayamaz; sistemin güvenliğini veya performansını tehlikeye atacak otomatik sorgular, saldırılar ve erişim denemelerinde bulunamaz. Aykırılık hâlinde Z Hesap hesabı askıya alma veya kapatma hakkına sahiptir.'
          ),
        ],
      },
      {
        title: '5. Entegrasyonlar ve Veriler',
        blocks: [
          p(
            `Hizmetler, Kullanıcı’nın yetkilendirdiği pazaryeri, e-ticaret, ERP ve kargo sistemlerinden alınan verilerle çalışır. Bu sistemlerden gelen verilerin doğruluğu ve güncelliği ile üçüncü taraf sistemlerdeki değişiklik ve kesintiler Z Hesap’ın kontrolü dışındadır. Kullanıcı’nın ticari verileri Kullanıcı’ya aittir ve ${doc('gizlilik')}’na uygun olarak işlenir.`
          ),
        ],
      },
      {
        title: '6. Analizlerin Niteliği',
        blocks: [
          p(
            'Hizmetlerde sunulan raporlar, hesaplamalar, öneriler ve yapay zekâ çıktıları karar destek amaçlıdır; yatırım, vergi, mali veya hukuki danışmanlık niteliği taşımaz. Bu çıktılara dayanılarak alınan ticari kararların sorumluluğu Kullanıcı’ya aittir.'
          ),
        ],
      },
      {
        title: '7. Fikri Mülkiyet',
        blocks: [
          p(
            'Hizmetlerdeki yazılım, tasarım, metin, logo, marka ve diğer tüm içerikler Z Hesap’a aittir veya lisans altında kullanılır. Z Hesap’ın yazılı izni olmadan kopyalanamaz, çoğaltılamaz veya dağıtılamaz. Üçüncü taraf platformlara ait marka ve içerikler ilgili sahiplerine aittir.'
          ),
        ],
      },
      {
        title: '8. Hizmet Sürekliliği ve Değişiklikler',
        blocks: [
          p(
            'Z Hesap, Hizmetlerin kesintisiz çalışması için makul özeni gösterir; planlı bakım çalışmaları mümkün olduğunca önceden duyurulur. Z Hesap, Hizmetlerin içeriğini ve özelliklerini geliştirme amacıyla değiştirebilir. Ücretli bir aboneliğin temel kapsamını daraltan değişiklikler, abonelik dönemi içinde Kullanıcı aleyhine uygulanmaz.'
          ),
        ],
      },
      {
        title: '9. Sorumluluğun Sınırlandırılması',
        blocks: [
          p(
            'Kanunen sınırlandırılamayan kast ve ağır ihmal hâlleri saklı kalmak üzere; Z Hesap’ın Hizmetlerden doğan toplam sorumluluğu, zararın doğduğu tarihten önceki 12 ay içinde Kullanıcı’nın Z Hesap’a ödediği toplam bedelle sınırlıdır. Z Hesap; kâr kaybı, ticari itibar kaybı ve dolaylı zararlardan sorumlu tutulamaz.'
          ),
        ],
      },
      {
        title: '10. Şartlarda Değişiklik',
        blocks: [
          p(
            'Z Hesap bu şartları güncelleyebilir. Güncel sürüm bu sayfada yayımlanır; kayıtlı kullanıcılar önemli değişikliklerden e-posta veya uygulama içi bildirimle haberdar edilir.'
          ),
        ],
      },
      {
        title: '11. Uygulanacak Hukuk ve Yetki',
        blocks: [
          p('Bu şartlar Türk hukukuna tabidir. Uyuşmazlıklarda İstanbul Anadolu Mahkemeleri ve İcra Daireleri yetkilidir.'),
        ],
      },
      {
        title: '12. İletişim',
        blocks: [p(`${company.name} — ${company.address} — ${mail} — ${company.phone}`)],
      },
    ],
  },
  {
    key: 'mesafeli',
    href: routes.yasal.mesafeli,
    title: titles.mesafeli,
    summary: 'Paket, bedel, ödeme, yenileme, paket değişikliği ve sözleşmenin sona ermesine ilişkin abonelik koşulları.',
    updated: UPDATED_LICENSE,
    intro: [
      'İşbu Mesafeli Satış ve Abonelik Sözleşmesi (“Sözleşme”), www.zhesap.com üzerinden veya Satıcı tarafından iletilen teklif aracılığıyla elektronik ortamda kurulur ve aşağıdaki taraflar arasında akdedilir.',
    ],
    sections: [
      {
        title: '1. Taraflar',
        blocks: [
          p('**SATICI**'),
          companyList({ phoneFirst: true }),
          p('**ALICI**'),
          p(
            'Sipariş sırasında beyan edilen unvan veya ad soyad, vergi kimlik numarası veya T.C. kimlik numarası, adres, telefon ve e-posta bilgileri esas alınır.'
          ),
        ],
      },
      {
        title: '2. Konu',
        blocks: [
          p(
            'Sözleşmenin konusu, Satıcı tarafından geliştirilen Z Hesap bulut tabanlı yazılımının (SaaS) Alıcı tarafından seçilen paket, modül ve ek haklar kapsamında kullanım hakkının Alıcı’ya sağlanması ve tarafların hak ve yükümlülüklerinin belirlenmesidir.'
          ),
        ],
      },
      {
        title: '3. Ticari Nitelik',
        blocks: [
          p(
            'Hizmetler yalnızca ticari veya mesleki amaçla kullanılmak üzere sunulur. Alıcı, hizmeti ticari veya mesleki faaliyeti kapsamında satın aldığını kabul ve beyan eder. Bu nedenle Sözleşme’ye 6102 sayılı Türk Ticaret Kanunu ve 6098 sayılı Türk Borçlar Kanunu hükümleri uygulanır; 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve ilgili yönetmelikler uygulanmaz.'
          ),
        ],
      },
      {
        title: '4. Hizmetin Kapsamı',
        blocks: [
          list(
            `**Paket:** Starter, Scale veya Signature paketlerinden Alıcı’nın seçtiği paket. Her paketin kullanıcı sayısı, dahil modülleri ve sipariş ile yapay zekâ hakları, satın alma anında [www.zhesap.com/fiyatlandirma](${routes.fiyatlar}) sayfasında veya Alıcı’ya iletilen teklifte belirtildiği gibidir.`,
            '**Ek haklar ve modüller:** ek kullanıcı, ek sipariş hakkı, modül marketi modülleri ve tek seferlik ek haklar, satın alındıkları anda geçerli koşullarla Sözleşme’nin parçası olur.',
            '**Teslim:** Hizmet, ödemenin onaylanmasının ardından Alıcı hesabının aktifleştirilmesiyle elektronik ortamda teslim edilmiş sayılır. Aktifleştirme otomatik olarak veya en geç 1 iş günü içinde yapılır.'
          ),
        ],
      },
      {
        title: '5. Bedel ve Ödeme',
        blocks: [
          list(
            'Fiyatlar Türk lirası cinsinden ve **nihai** olup ayrıca KDV eklenmez.',
            'Ödeme, Satıcı’nın anlaşmalı ödeme kuruluşu aracılığıyla kredi kartı veya banka kartı ile ya da Satıcı’nın kabul etmesi hâlinde havale/EFT ile yapılır. Taksit seçenekleri ödeme ekranında veya teklifte belirtilir. Kart bilgileri Satıcı tarafından saklanmaz.',
            'Fatura, elektronik fatura veya e-arşiv fatura olarak Alıcı’nın beyan ettiği e-posta adresine gönderilir.'
          ),
        ],
      },
      {
        title: '6. Lisans Süresi',
        blocks: [
          p(
            '**6.1. Yıllık lisans:** Paketler yıllık lisans olarak sunulur. Yıllık lisans 12 aylık kullanım hakkıdır ve lisans süresi boyunca fiyat sabittir. Fiyatlandırma sayfasında gösterilen aylık tutar, yıllık lisans bedelinin aylık karşılığıdır. Ödeme peşin veya ödeme ekranında ya da teklifte sunulan taksit seçenekleriyle yapılır; taksitli ödemede taksitler lisans süresinden bağımsız olarak bankaca tahsil edilir.'
          ),
          p(
            '**6.2. Teklifle belirlenen lisans süresi:** Satıcı’nın Alıcı’ya ilettiği yazılı teklifte 12 aydan farklı bir lisans süresi (ör. 3 veya 6 ay) belirlenebilir. Bu durumda lisans süresi, bedeli ve ödeme planı teklifte yer alır ve fiyat lisans süresi boyunca sabittir.'
          ),
          p(
            '**6.3. Haklar:** Paket kapsamındaki aylık sipariş ve yapay zekâ hakları her ay lisans başlangıç gününde yenilenir. Kullanılmayan haklar sonraki döneme devretmez. Tek seferlik ek sipariş hakları satın alındıkları tarihten itibaren 30 gün geçerlidir.'
          ),
        ],
      },
      {
        title: '7. Yenileme',
        blocks: [
          list(
            'Satıcı, lisans bitiminden en az 30 gün önce yenileme bedelini Alıcı’ya bildirir.',
            'Alıcı, lisans bitiminden en az 15 gün önce aksini bildirmedikçe lisans, yenileme tarihindeki güncel fiyat listesi üzerinden 12 ay süreyle yenilenir.',
            'Madde 6.2 uyarınca 12 aydan kısa süreli lisanslarda Alıcı, lisans bitiminden en az 15 gün önce lisansı aynı süreyle uzatmayı, yıllık lisansa geçmeyi veya lisansın yenilenmemesini seçebilir. Alıcı bir tercih bildirmezse lisans, Satıcı’nın bildirdiği yenileme bedeli üzerinden aynı süreyle yenilenir. Yıllık lisansa geçişte güncel fiyat listesi uygulanır.'
          ),
        ],
      },
      {
        title: '8. Paket Değişikliği',
        blocks: [
          list(
            '**Yükseltme:** Alıcı paketini dilediği zaman yükseltebilir. Yükseltmede yalnızca sözleşmenin kalan süresine düşen fiyat farkı gün bazında (kıst) hesaplanarak tahsil edilir; yeni kapasite derhâl tanımlanır.',
            '**Düşürme:** Paket veya kapasite düşürme yalnızca yenileme döneminde yapılabilir ve son yükseltme tarihinden itibaren en az 6 ay geçmiş olmasını gerektirir.'
          ),
        ],
      },
      {
        title: '9. Ödeme Alınamaması',
        blocks: [
          p(
            'Yenileme tahsilatı başarısız olursa Satıcı, 2 gün arayla en fazla 3 kez yeniden deneme yapar ve Alıcı’yı e-posta ile bilgilendirir. Tahsilat yine gerçekleşmezse Alıcı’nın hizmete erişimi, ödeme yapılana kadar kısıtlanır.'
          ),
        ],
      },
      {
        title: '10. İptal ve İade',
        blocks: [
          p(
            `İptal ve iade koşulları ${doc('iptalIade')} metninde düzenlenmiş olup bu Sözleşme’nin ayrılmaz parçasıdır. Hizmet ticari amaçla satın alındığından, ödenen bedeller iade edilmez.`
          ),
        ],
      },
      {
        title: '11. Alıcı’nın Yükümlülükleri',
        blocks: [
          list(
            'Hizmeti yalnızca kendi ticari faaliyeti için kullanır; devredemez, kiralayamaz, alt lisanslayamaz veya üçüncü kişilere satamaz.',
            'Kullanıcı hesaplarının, yetkilerin, şifrelerin, API anahtarlarının ve entegrasyon bilgilerinin güvenliğinden sorumludur. Her kullanıcı hesabı aynı anda tek oturumla kullanılabilir.',
            'Bağladığı sistemlerden aktarılan verilerin doğruluğundan sorumludur.',
            'Hizmete erişim için gereken internet bağlantısı ve donanımı sağlar.',
            'Hizmet kapsamında kendi müşterilerine ait kişisel verileri işletmesi bakımından veri sorumlusudur; Satıcı bu veriler bakımından yalnızca veri işleyen olarak hareket eder.'
          ),
        ],
      },
      {
        title: '12. Veriler ve Fikri Mülkiyet',
        blocks: [
          list(
            'Yazılım, kodlar, tasarım ve tüm içeriklere ilişkin fikri ve sınai mülkiyet hakları Satıcı’ya aittir. Alıcı yazılımı kopyalayamaz, değiştiremez veya tersine mühendislik uygulayamaz.',
            'Alıcı’nın ticari verileri Alıcı’ya aittir. Satıcı, bu verileri yalnızca hizmetin sunulması amacıyla işler. Satıcı, tüm kullanıcılardan elde edilen verileri anonimleştirerek ve hiçbir Alıcı veya kişiyle ilişkilendirilemeyecek biçimde toplulaştırarak sektör ortalamaları, kıyaslama analizleri ve hizmetin geliştirilmesi amacıyla kullanabilir.',
            `Kişisel verilerin işlenmesi ${doc('kvkk')}, ${doc('gizlilik')} ve ilgili politikalara tabidir.`
          ),
        ],
      },
      {
        title: '13. Sorumluluğun Sınırları',
        blocks: [
          p(
            'Platform bir analiz ve karar destek aracıdır; sunulan raporlar ve analizler yatırım, vergi veya mali danışmanlık niteliği taşımaz ve bu çıktılara dayanılarak alınan kararların sorumluluğu Alıcı’ya aittir. Kanunen sınırlandırılamayan kast ve ağır ihmal hâlleri saklı kalmak üzere, Satıcı’nın bu Sözleşme’den doğan toplam sorumluluğu, zararın doğduğu tarihten önceki 12 ay içinde Alıcı’nın ödediği toplam bedelle sınırlıdır. Satıcı; kâr kaybı, ticari itibar kaybı ve dolaylı zararlardan sorumlu değildir.'
          ),
        ],
      },
      {
        title: '14. Mücbir Sebep',
        blocks: [
          p(
            'Doğal afet, yangın, salgın, savaş, terör, grev, siber saldırı, internet altyapısı veya üçüncü taraf sağlayıcı kaynaklı kesintiler gibi tarafların kontrolü dışındaki olaylar nedeniyle yükümlülüklerin geç veya hiç ifa edilememesinden taraflar sorumlu tutulamaz.'
          ),
        ],
      },
      {
        title: '15. Sözleşmenin Sona Ermesi ve Veriler',
        blocks: [
          p(
            'Abonelik sona erdiğinde veya iptal edildiğinde Alıcı’nın hesabındaki veriler, bitiş tarihinden itibaren 15 gün boyunca saklanır; Alıcı bu süre içinde aboneliğini yeniden başlatırsa kaldığı yerden devam eder. Z Hesap’taki ham veriler Alıcı’nın satış kanallarından çekilen ve bu kanallarda bulunmaya devam eden verilerdir; Z Hesap tarafından üretilen hesaplama, analiz ve raporlar platforma özgü olup dışa aktarılamaz. 15 günlük sürenin sonunda tüm veriler yedekler dâhil kalıcı olarak silinir; mevzuat gereği saklanması zorunlu fatura ve mali kayıtlar yasal süre boyunca saklanır. Ayrıntılar **Veri Saklama ve İmha Politikası**’nda düzenlenir.'
          ),
        ],
      },
      {
        title: '16. Bildirimler ve Delil',
        blocks: [
          p(
            'Taraflar arasındaki bildirimler, Alıcı’nın kayıtlı e-posta adresi ve uygulama içi bildirimler aracılığıyla yapılır. Satıcı’nın elektronik kayıtları ve sistem logları, HMK m.193 uyarınca kesin delil niteliğindedir.'
          ),
        ],
      },
      {
        title: '17. Yetkili Mahkeme',
        blocks: [p('Bu Sözleşme’den doğan uyuşmazlıklarda İstanbul Anadolu Mahkemeleri ve İcra Daireleri yetkilidir.')],
      },
      {
        title: '18. Yürürlük',
        blocks: [
          p(
            `Alıcı, bu Sözleşme’yi ve Sözleşme’nin ayrılmaz parçası olan ${doc('onBilgilendirme')}, ${doc('iptalIade')}, ${doc('kullanim')}, ${doc('kvkk')} ve ${doc('gizlilik')}’nı elektronik ortamda okuyup onayladığını beyan eder. Sözleşme, Alıcı’nın onayı ile yürürlüğe girer.`
          ),
        ],
      },
    ],
  },
  {
    key: 'onBilgilendirme',
    href: routes.yasal.onBilgilendirme,
    title: titles.onBilgilendirme,
    summary: 'Satın almadan önce bilmeniz gerekenler: satıcı bilgileri, hizmetin nitelikleri, bedel, teslim ve iptal.',
    updated: UPDATED_LICENSE,
    intro: [
      `Bu form, Z Hesap aboneliğinin satın alınmasından önce Alıcı’nın bilgilendirilmesi amacıyla hazırlanmıştır ve ${doc('mesafeli')}’nin ayrılmaz parçasıdır.`,
    ],
    sections: [
      { title: '1. Satıcı Bilgileri', blocks: [companyList({ phoneFirst: true })] },
      {
        title: '2. Hizmetin Temel Nitelikleri',
        blocks: [
          p(
            `Z Hesap; e-ticaret işletmeleri için kârlılık analizi, finans ve mutabakat kontrolü, kampanya kârlılığı, stok ve rekabet analizi ile yapay zekâ destekli karar destek hizmetleri sunan bulut tabanlı bir yazılımdır. Seçilen paketin kullanıcı sayısı, modülleri ve hakları ödeme ekranında ve [www.zhesap.com/fiyatlandirma](${routes.fiyatlar}) sayfasında yer alır.`
          ),
        ],
      },
      {
        title: '3. Bedel ve Ödeme',
        blocks: [
          list(
            'Ödeme ekranında gösterilen tutar **nihai tutardır**; ayrıca KDV eklenmez.',
            'Ödeme kredi kartı veya banka kartı ile ya da Satıcı’nın kabul etmesi hâlinde havale/EFT ile yapılır. Taksit seçenekleri ve varsa taksit farkı ödeme ekranında veya teklifte ayrıca gösterilir.',
            'Paketler yıllık lisans olarak sunulur ve fiyat 12 ay boyunca sabittir. Satıcı’nın teklifinde farklı bir lisans süresi belirlenmişse fiyat o süre boyunca sabittir.',
            'Lisans, bitiminden en az 15 gün önce aksi bildirilmedikçe yenilenir: yıllık lisans güncel fiyat listesi üzerinden 12 ay, teklifle belirlenen kısa süreli lisans ise aynı süreyle. Kısa süreli lisansta Alıcı dilerse süre sonunda yıllık lisansa geçebilir.'
          ),
        ],
      },
      {
        title: '4. Teslim',
        blocks: [
          p(
            'Hizmet, ödemenin onaylanmasının ardından hesabın aktifleştirilmesiyle elektronik ortamda teslim edilir. Aktifleştirme otomatik olarak veya en geç 1 iş günü içinde yapılır.'
          ),
        ],
      },
      {
        title: '5. Ticari Nitelik ve Cayma Hakkı',
        blocks: [
          p(
            'Hizmet yalnızca ticari veya mesleki amaçla kullanılmak üzere sunulur. Alıcı hizmeti ticari veya mesleki faaliyeti kapsamında satın aldığından, 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği uygulanmaz; **cayma hakkı bulunmamaktadır** ve ödenen bedeller iade edilmez.'
          ),
        ],
      },
      {
        title: '6. İptal',
        blocks: [
          p(
            `Lisans, süresi dolmadan sona erdirilemez. Lisansın yenilenmemesi için bitiminden en az 15 gün önce bildirimde bulunulması yeterlidir; lisans süre sonunda kapanır. Ayrıntılar: ${doc('iptalIade')}.`
          ),
        ],
      },
      {
        title: '7. Şikâyet ve Uyuşmazlık',
        blocks: [
          p(
            `Talep ve şikâyetlerinizi ${mail} adresine iletebilirsiniz. Uyuşmazlıklarda İstanbul Anadolu Mahkemeleri ve İcra Daireleri yetkilidir.`
          ),
        ],
      },
    ],
  },
  {
    key: 'iptalIade',
    href: routes.yasal.iptalIade,
    title: titles.iptalIade,
    summary: 'Lisansın yenilenmemesi, iade kuralları, paket düşürme ve iptal sonrası verileriniz.',
    updated: UPDATED_LICENSE,
    intro: [
      `Bu metin, Z Hesap Yazılım Anonim Şirketi (“Z Hesap”) tarafından sunulan aboneliklerin iptal ve iade koşullarını düzenler ve ${doc('mesafeli')}’nin ayrılmaz parçasıdır.`,
    ],
    sections: [
      {
        title: '1. Ticari Nitelik',
        blocks: [
          p(
            'Z Hesap hizmetleri yalnızca ticari veya mesleki amaçla kullanılmak üzere sunulan bir yazılım kullanım hakkıdır. Bu nedenle 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve ilgili mevzuat bu satışlara uygulanmaz ve cayma hakkı bulunmaz.'
          ),
        ],
      },
      {
        title: '2. İade',
        blocks: [
          p(
            'Hizmet bedelleri, kullanım ve kiralama bedeli niteliğinde olduğundan **satın alınan paket, modül, ek kullanıcı, ek sipariş hakkı ve diğer ek hakların bedelleri iade edilmez.** Kısmen kullanılmış veya hiç kullanılmamış süreler için de iade yapılmaz.'
          ),
        ],
      },
      {
        title: '3. Lisans Süresi ve Yenilememe',
        blocks: [
          list(
            'Yıllık lisans 12 aylık kullanım hakkıdır ve süre dolmadan feshedilemez. Teklifle belirlenen 3 veya 6 aylık lisanslar için de aynı kural geçerlidir; bu lisanslar süre sonunda aynı süreyle uzatılabilir, yıllık lisansa geçirilebilir veya yenilenmeyebilir.',
            `Lisansınızın yenilenmemesini istiyorsanız, lisans bitiminden en az 15 gün önce uygulama içinden veya ${mail} adresine bildirimde bulunmanız yeterlidir. Bu durumda lisansınız süre sonunda kapanır ve sonraki dönem için tahsilat yapılmaz.`,
            'Taksitli ödemelerde taksitler, iptal durumunda da bankanız tarafından tahsil edilmeye devam eder.'
          ),
        ],
      },
      {
        title: '4. Modüller ve Ek Kullanıcılar',
        blocks: [
          p(
            'Modül marketinden aylık olarak alınan modüller ve ek kullanıcılar taahhütsüzdür; iptal, bir sonraki lisans gününde geçerli olur. Yıllık olarak alınanlar dönem sonuna kadar geçerlidir.'
          ),
        ],
      },
      {
        title: '5. Paket Düşürme',
        blocks: [
          p(
            'Paket veya kapasite düşürme yalnızca yenileme döneminde ve son yükseltme tarihinden itibaren en az 6 ay geçmişse yapılabilir. Düşürme işlemi iade doğurmaz.'
          ),
        ],
      },
      {
        title: '6. Ödeme Alınamaması',
        blocks: [
          p(
            'Yenileme tahsilatı başarısız olursa 2 gün arayla en fazla 3 kez yeniden denenir. Tahsilat yine gerçekleşmezse hizmete erişim, ödeme yapılana kadar kısıtlanır.'
          ),
        ],
      },
      {
        title: '7. İptal Sonrası Verileriniz',
        blocks: [
          p(
            'Aboneliğiniz sona erdikten sonra verileriniz 15 gün boyunca saklanır; bu süre içinde aboneliğinizi yeniden başlatırsanız kaldığınız yerden devam edersiniz. Z Hesap tarafından üretilen hesaplama ve analizler dışa aktarılamaz. 15 günün sonunda verileriniz **Veri Saklama ve İmha Politikası**’na göre kalıcı olarak silinir.'
          ),
        ],
      },
      {
        title: '8. Hizmetin Satıcı Kaynaklı Olarak Verilememesi',
        blocks: [
          p(
            'Hizmetin Z Hesap kaynaklı bir nedenle uzun süre hiç sunulamaması hâlinde, Alıcı’nın kanundan doğan hakları saklıdır.'
          ),
        ],
      },
      { title: '9. İletişim', blocks: [p(`${mail} — ${company.phone}`)] },
    ],
  },
  {
    key: 'guvenlik',
    href: routes.yasal.guvenlik,
    title: titles.guvenlik,
    summary: 'Kişisel ve ticari verilerin güvenliği için uyguladığımız teknik ve idari tedbirler.',
    updated: UPDATED,
    intro: [
      'Bu Politika, Z Hesap Yazılım Anonim Şirketi (“Z Hesap”) tarafından işlenen kişisel ve ticari verilerin güvenliği için uygulanan teknik ve idari tedbirleri açıklar ve KVKK m.12 ile uyumludur.',
    ],
    sections: [
      {
        title: '1. Kapsam',
        blocks: [
          p(
            'Web sitesi, Z Hesap web uygulaması ve tarayıcı eklentisi dâhil tüm hizmetlerde işlenen verilerin gizliliği, bütünlüğü ve erişilebilirliği.'
          ),
        ],
      },
      {
        title: '2. Teknik Tedbirler',
        blocks: [
          list(
            '**Şifreleme:** Veriler aktarım sırasında SSL/TLS ile, saklama sırasında uygun şifreleme yöntemleriyle korunur. Parolalar geri döndürülemez biçimde özetlenerek (hash) saklanır.',
            '**Erişim kontrolü:** Rol tabanlı yetkilendirme ve “en az yetki” ilkesi uygulanır; erişimler kayıt altına alınır.',
            '**Oturum güvenliği:** Her kullanıcı hesabı aynı anda yalnızca tek oturumla kullanılabilir.',
            '**Ağ güvenliği:** Güvenlik duvarı, ağ ayrıştırması ve saldırı önleme tedbirleri kullanılır.',
            '**Log ve izleme:** Sistem ve erişim kayıtları tutulur, güvenlik olayları izlenir.',
            '**Yedekleme:** Veriler düzenli olarak yedeklenir, geri yükleme süreçleri test edilir.',
            '**Ortam ayrımı ve güncelleme:** Üretim ve test ortamları ayrıdır; yazılım bileşenleri güncel tutulur, güvenlik yamaları uygulanır.'
          ),
        ],
      },
      {
        title: '3. İdari Tedbirler',
        blocks: [
          list(
            'Çalışanlar için gizlilik yükümlülükleri ve veri koruma farkındalığı,',
            'Yetki matrisi ve erişim taleplerinin kontrollü yönetimi, görevden ayrılan personelin erişimlerinin derhâl kaldırılması,',
            'Hizmet sağlayıcılarla Veri İşleme Sözleşmesi (DPA) ve gerektiğinde Standart Sözleşme Hükümleri,',
            'Amaç ve süreyle sınırlı işleme.'
          ),
        ],
      },
      {
        title: '4. Kimlik Doğrulama',
        blocks: [p('Güçlü parola gereksinimleri uygulanır ve iki faktörlü kimlik doğrulama (2FA) desteklenir.')],
      },
      {
        title: '5. Entegrasyon Güvenliği',
        blocks: [
          p(
            'Pazaryeri ve e-ticaret hesaplarına erişim, ilgili platformların resmî API’leri ve kullanıcının verdiği yetki ile sağlanır. Entegrasyon bilgileri yalnızca veri senkronizasyonu amacıyla kullanılır ve erişim kontrolüne tabidir.'
          ),
        ],
      },
      {
        title: '6. Yapay Zekâ Güvenliği',
        blocks: [
          p(
            'Yapay zekâ destekli özelliklerde kişisel veri paylaşılmaz; kişiyi tanımlayabilecek alanlar işleme öncesinde anonimleştirilir veya maskelenir. Sağlayıcılarla “verilerin model eğitiminde kullanılmaması” taahhüdü bulunur.'
          ),
        ],
      },
      {
        title: '7. Ödeme Güvenliği',
        blocks: [
          p('Ödemeler PCI-DSS uyumlu ödeme kuruluşu üzerinden alınır; kart bilgileri Z Hesap sistemlerinde saklanmaz.'),
        ],
      },
      {
        title: '8. İhlal Yönetimi',
        blocks: [
          p(
            'Bir veri ihlali tespit edildiğinde olay değerlendirilir, etkisi sınırlandırılır ve düzeltici önlemler alınır. İhlal, öğrenildiği andan itibaren en geç 72 saat içinde Kişisel Verileri Koruma Kurulu’na, gerektiğinde ilgili kişilere bildirilir.'
          ),
        ],
      },
      {
        title: '9. Gözden Geçirme',
        blocks: [
          p('Güvenlik tedbirlerinin etkinliği düzenli olarak gözden geçirilir ve bu Politika gerektiğinde güncellenir.'),
        ],
      },
      { title: '10. İletişim', blocks: [p(`Güvenlikle ilgili bildirimleriniz için: ${mail}`)] },
    ],
  },
];

/**
 * İçerik backend'indeki (zhesap_main_be) belge anahtarı. Site içi anahtar camelCase; CMS'te
 * kebab-case. Adresler (routes.yasal) kodda kalır, CMS yalnız metni verir.
 */
export const legalCmsKey: Record<LegalKey, string> = {
  kvkk: 'kvkk',
  gizlilik: 'gizlilik',
  cerez: 'cerez',
  kullanim: 'kullanim',
  mesafeli: 'mesafeli',
  onBilgilendirme: 'on-bilgilendirme',
  iptalIade: 'iptal-iade',
  guvenlik: 'guvenlik',
};

/** Şirket künyesinin statik hâli; CMS'te site ayarlarından gelir. */
export const legalCompany = company;

export const legalByKey = Object.fromEntries(legalDocs.map((d) => [d.key, d])) as Record<LegalKey, LegalDoc>;

/** Adresten belgeyi bulur; router bu adresleri `routes.yasal`'dan okur. */
export function findLegalDoc(pathname: string) {
  return legalDocs.find((d) => d.href === pathname);
}

/** Bölüm başlığından çapa kimliği: "3. Hesap ve Güvenlik" → "hesap-ve-guvenlik". */
export function sectionId(title: string) {
  return title
    .replace(/^\d+\.\s*/, '')
    .replace(/\(.*?\)/g, '')
    .toLocaleLowerCase('tr-TR')
    .replace(/[çÇ]/g, 'c')
    .replace(/[ğĞ]/g, 'g')
    .replace(/[ıİ]/g, 'i')
    .replace(/[öÖ]/g, 'o')
    .replace(/[şŞ]/g, 's')
    .replace(/[üÜ]/g, 'u')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
